import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Briefcase, ClipboardList, FileClock, TrendingUp, CheckCircle2, XCircle, PauseCircle } from "lucide-react";

const SERVICE_ICONS = {
  "ITR": "📊", "GST": "🧾", "MSME Loan": "🏢", "Insurance": "🛡️", "Other": "📁",
};

export default function ServiceDashboard() {
  const { serviceType } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/services/${encodeURIComponent(serviceType)}/dashboard`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Access denied"));
  }, [serviceType]);

  if (err) return <div className="card p-8 text-center text-red-600">{err}</div>;
  if (!data) return <div>Loading…</div>;

  const { kpi, status_chart, recent_leads } = data;

  return (
    <div className="space-y-6" data-testid="service-dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Briefcase size={12} /> Service</div>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2">
          <span className="text-3xl">{SERVICE_ICONS[serviceType] || "📁"}</span>
          {serviceType} Leads
        </h1>
        <p className="text-sm text-muted-foreground">Dedicated view for {serviceType} pipeline</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { icon: ClipboardList, label: "Total", value: kpi.total_leads, tint: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
          { icon: FileClock, label: "New Cases", value: kpi.new_cases, tint: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" },
          { icon: TrendingUp, label: "In Progress", value: kpi.wip, tint: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
          { icon: CheckCircle2, label: "Completed", value: kpi.completed, tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
          { icon: PauseCircle, label: "On Hold", value: kpi.on_hold, tint: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
          { icon: XCircle, label: "Rejected", value: kpi.rejected, tint: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" },
        ].map((k) => (
          <div key={k.label} className="kpi-card" data-testid={`svc-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{k.label}</div>
                <div className="mt-2 font-heading text-3xl font-extrabold tabular">{k.value}</div>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.tint}`}><k.icon size={18} /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-5">
        <h3 className="font-heading font-bold text-lg mb-3">Status Breakdown</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={status_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="value" fill="#0052CC" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Recent {serviceType} Leads</h3>
          <Link to={`/leads?service=${encodeURIComponent(serviceType)}`} className="text-sm link-blue">View all →</Link>
        </div>
        <table className="table-clean">
          <thead><tr><th>Lead ID</th><th>Client</th><th>Phone</th><th>Connector</th><th>Assigned</th><th>Status</th><th>Date</th></tr></thead>
          <tbody data-testid="svc-recent-leads">
            {recent_leads.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No {serviceType} leads yet</td></tr>}
            {recent_leads.map((l) => (
              <tr key={l.id}>
                <td><Link to={`/leads/${l.id}`} className="font-mono link-blue">{l.lead_number}</Link></td>
                <td className="font-semibold">{l.client_name}</td>
                <td className="tabular">{l.phone}</td>
                <td>{l.connector_name}</td>
                <td>{l.assigned_to_name || <span className="text-muted-foreground">—</span>}</td>
                <td><StatusBadge status={l.status} /></td>
                <td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
